import add from 'date-fns/add';
import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import {
  AgendaTimeline,
  AgendaTimelineGroup,
  DateNavigator,
  ShowAllAppointmentsToggle,
  groupOverlapItems,
  useShowAllAppointmentsToggle,
} from '@eluve/blocks';
import {
  Button,
  Calendar,
  Content,
  DetailsSidebar,
  DetailsSidebarProvider,
  DetailsSidebarTrigger,
  Grid,
  Header,
  Heading,
  Icon,
  Separator,
  SidebarTrigger,
  Stack,
  useToast,
} from '@eluve/components';
import { useAppointmentCounts } from '@eluve/frontend-appointment-hooks';
import { useEHRSyncStore } from '@eluve/frontend-feature-ehr-sync';
import { useTranslation } from '@eluve/i18n';
import { InterstitialSidePanels } from '@eluve/interstitials';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { AppointmentAgendaTimelineItem } from './components/AppointmentAgendaTimelineItem';
import { AppointmentsLoader } from './components/AppointmentsLoader';
import { EhrDataImporter } from './components/EhrDataImporter';
import { EhrOnboarding } from './components/EhrOnboarding';
import { EmptyHomePageState } from './components/EmptyHomePageState';
import { NewAppointmentButton } from './components/NewAppointmentButton';
import { NoAppointmentPlaceholder } from './components/NoAppointmentPlaceholder';
import { useCalendarAppointmentData } from './useCalendarAppointmentData';
import { useImportDataFromEhr } from './useImportDataFromEhr';

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const lastSuccessfulEhrSyncAt = useEHRSyncStore(
    (s) => s.lastSuccessfulEhrSyncAt,
  );
  const userId = useUserIdFromSession();
  const [showAllAppointments] = useShowAllAppointmentsToggle();
  const tenantId = useTenantIdFromParams();

  const locationsEnabled = useIsFeatureFlagEnabled('LOCATIONS');

  const { data, loading, error, dateSearchParam, setDate, date } =
    useCalendarAppointmentData();
  const toast = useToast();

  const {
    totalAppointmentsCount,
    upcomingAppointmentsCount,
    refetch: refetchAppointmentCountData,
    loading: loadingAppointmentCountData,
  } = useAppointmentCounts({ days: 7, fromDate: dateSearchParam });

  const { isSupportedBrowser } = useImportDataFromEhr(tenantId!);
  const displayEmptyHomePageState =
    locationsEnabled &&
    isSupportedBrowser &&
    !showAllAppointments &&
    (!totalAppointmentsCount || !upcomingAppointmentsCount);

  const appointments = useMemo(() => {
    const appointments = data?.appointments ?? [];
    if (showAllAppointments) {
      return appointments;
    }
    return appointments.filter((a) => a.userId === userId);
  }, [data?.appointments, showAllAppointments, userId]);

  const appointmentsGrouped = useMemo(
    () =>
      groupOverlapItems(appointments, (a) => [
        new Date(a.startDate),
        new Date(a.endDate ?? add(new Date(a.startDate), { hours: 1 })),
      ]),
    [appointments],
  );

  useEffect(
    function displayToastOnError() {
      if (error) {
        toast.error(t('home.toasts.failedToRetrieveAppointments'));
      }
    },
    [error, toast, t],
  );

  useEffect(
    function refetchOnEhrSync() {
      if (lastSuccessfulEhrSyncAt) {
        refetchAppointmentCountData();
      }
    },
    [lastSuccessfulEhrSyncAt, refetchAppointmentCountData],
  );

  return (
    <>
      <Helmet>
        <title>Home | Eluve</title>
      </Helmet>
      <DetailsSidebarProvider>
        <Header>
          <Stack gap={2} direction="horizontal">
            <SidebarTrigger />
            <Heading>{t('sidebar.overview')}</Heading>
          </Stack>
          <Stack justify="end" gap={2} direction="horizontal">
            <NewAppointmentButton />
            <DetailsSidebarTrigger>
              <Button variant="outline">
                <Icon name="Calendar" size="auto" />
              </Button>
            </DetailsSidebarTrigger>
          </Stack>
        </Header>
        <Content>
          <Stack className="overflow-auto" fullHeight>
            <Stack
              className="border-b border-border-primary p-3 sm:flex-row sm:items-center"
              gap={4}
            >
              <ShowAllAppointmentsToggle />
              <DateNavigator
                onDateChanged={(newDate) => setDate(newDate)}
                startingDate={date}
                key={date.toDateString()}
              />
            </Stack>
            <Grid
              columns={1}
              rows={1}
              fullHeight
              className='relative overflow-y-auto *:relative *:z-auto *:col-start-1 *:row-start-1 [data-empty="true"]:overflow-hidden'
              data-empty={displayEmptyHomePageState}
            >
              <AgendaTimeline day={date} showGrid={!displayEmptyHomePageState}>
                {appointmentsGrouped.map((appointments, index) => {
                  if (appointments.length === 1) {
                    // We know this is safe because we're checking the length
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const appointment = appointments[0]!;
                    return (
                      <AppointmentAgendaTimelineItem
                        key={appointment.id}
                        appointmentId={appointment.id}
                      />
                    );
                  }

                  return (
                    <AgendaTimelineGroup key={index}>
                      {appointments.map((a) => (
                        <AppointmentAgendaTimelineItem
                          key={a!.id}
                          appointmentId={a!.id}
                        />
                      ))}
                    </AgendaTimelineGroup>
                  );
                })}
              </AgendaTimeline>
              {loading && loadingAppointmentCountData && <AppointmentsLoader />}
              {!displayEmptyHomePageState &&
                data?.appointments?.length === 0 && (
                  <NoAppointmentPlaceholder />
                )}
              {displayEmptyHomePageState && <EmptyHomePageState />}
            </Grid>
          </Stack>
        </Content>
        <DetailsSidebar title={t('home.calendarSidebar.title')}>
          <Stack align="center">
            <Stack align="center" gap={2}>
              <Calendar mode="single" selected={date} onSelect={setDate} />
            </Stack>
            <Separator className="my-2" />
            <Stack align="center" className="p-4 pt-2" gap={2}>
              {isSupportedBrowser && <EhrDataImporter />}
              {isSupportedBrowser && <EhrOnboarding />}
              <InterstitialSidePanels />
            </Stack>
          </Stack>
        </DetailsSidebar>
      </DetailsSidebarProvider>
    </>
  );
};
